import { IUser } from "./models/user";
import { Action } from "redux";
import produce from "immer";
import { ILoginResponse } from "./auth.actions";

export interface IAuthState {
  currentUser: IUser | undefined;
  isLoggingIn: boolean;
  loginFailed: boolean;
  changePasswordFail: boolean;
  mobileAppUnauthorized: boolean;
}

const getCurrentUser = (): IUser | undefined => {
  if (localStorage.getItem("currentUser") !== null) {
    // @ts-ignore
    return JSON.parse(localStorage.getItem("currentUser"));
  }
  return undefined;
};

const initialState: IAuthState = {
  currentUser: getCurrentUser(),
  isLoggingIn: false,
  loginFailed: false,
  changePasswordFail: false,
  mobileAppUnauthorized: false,
};

const LOGIN = "AUTH/LOGIN";
const LOGOUT = "AUTH/LOGOUT";
const LOGIN_SUCCESS = "AUTH/LOGIN_SUCCESS";
const LOGIN_ERROR = "AUTH/LOGIN_ERROR";
const UNAUTHORIZED = "AUTH/UNAUTHORIZED";
const MOBILE_APP_UNAUTHORIZED = "AUTH/MOBILE_APP_UNAUTHORIZED";
const CHANGEPASSWORD_ERROR = "AUTH/CHANGEPASSWORD_ERROR";
const CHANGEPASSWORD_SUCCESS = "AUTH/CHANGEPASSWORD_SUCCESS";
const REFRESH_TOKEN = "AUTH/REFRESH_TOKEN";

export interface ILoginAction {
  type: typeof LOGIN;
}
export interface ILogoutAction {
  type: typeof LOGOUT;
}
export interface ILoginSuccessAction {
  type: typeof LOGIN_SUCCESS;
  payload: IUser;
}
export interface ILoginErrorAction {
  type: typeof LOGIN_ERROR;
}
export interface IUnauthorizedAction {
  type: typeof UNAUTHORIZED;
}
export interface IMobileAppUnauthorized {
  type: typeof MOBILE_APP_UNAUTHORIZED;
}
export interface IChangePasswordErrorAction {
  type: typeof CHANGEPASSWORD_ERROR;
}
export interface IChangePasswordSuccessAction {
  type: typeof CHANGEPASSWORD_SUCCESS;
}
export interface IRefreshTokenAction {
  type: typeof REFRESH_TOKEN;
  payload: ILoginResponse;
}

export const actionCreatorAuth = {
  login: { type: LOGIN } as ILoginAction,
  logout: { type: LOGOUT } as ILogoutAction,
  loginSuccess: (user: IUser) =>
    ({ type: LOGIN_SUCCESS, payload: user } as ILoginSuccessAction),
  loginError: { type: LOGIN_ERROR } as ILoginErrorAction,
  unauthorized: { type: UNAUTHORIZED } as IUnauthorizedAction,
  mobileAppUnauthorized: {type: MOBILE_APP_UNAUTHORIZED} as IMobileAppUnauthorized,
  changePasswordError: () =>
    ({ type: CHANGEPASSWORD_ERROR } as IChangePasswordErrorAction),
  changePasswordSuccess: () =>
    ({ type: CHANGEPASSWORD_SUCCESS } as IChangePasswordSuccessAction),
  refreshToken: (user: ILoginResponse) => ({ type: REFRESH_TOKEN, payload: user } as IRefreshTokenAction),
};

export const authReducer = (state = initialState, action: Action<string>) =>
  produce(state, (draft) => {
    switch (action.type) {
      case LOGIN:
        draft.isLoggingIn = true;
        draft.loginFailed = false;
        break;
      case LOGIN_SUCCESS:
        const loginSuccessAction = action as ILoginSuccessAction;
        draft.mobileAppUnauthorized = false;
        draft.isLoggingIn = false;
        draft.loginFailed = false;
        draft.currentUser = loginSuccessAction.payload;
        localStorage.setItem(
          "currentUser",
          JSON.stringify(loginSuccessAction.payload)
        );
        localStorage.removeItem("wasUserType");
        break;
      case LOGIN_ERROR:
        draft.mobileAppUnauthorized = false;
        draft.isLoggingIn = false;
        draft.loginFailed = true;
        break;
      case LOGOUT:
        localStorage.setItem(
          "wasUserType",
          JSON.stringify(draft.currentUser!.role)
        );
        draft.isLoggingIn = false;
        draft.loginFailed = false;
        draft.currentUser = undefined;
        localStorage.removeItem("currentUser");
        localStorage.removeItem("domainCode");
        break;
      case UNAUTHORIZED:
        localStorage.setItem(
          "wasUserType",
          JSON.stringify(draft.currentUser!.role)
        );
        draft.currentUser = undefined;
        draft.loginFailed = false;
        draft.isLoggingIn = false;
        localStorage.removeItem("currentUser");
        localStorage.removeItem("domainCode");
        break;
      case MOBILE_APP_UNAUTHORIZED:
        draft.mobileAppUnauthorized = true;
        draft.isLoggingIn = false;
        draft.loginFailed = true;
        localStorage.removeItem("currentUser");
        localStorage.removeItem("domainCode");
        break;
      case CHANGEPASSWORD_ERROR:
        draft.changePasswordFail = true;
        break;
      case CHANGEPASSWORD_SUCCESS:
        draft.changePasswordFail = false;
        break;
      case REFRESH_TOKEN:
        const refreshTokenAction = action as IRefreshTokenAction;
        if (!draft.currentUser) break;
        draft.currentUser.token = refreshTokenAction.payload.access_token;
        draft.currentUser.refresh_token = refreshTokenAction.payload.refresh_token;
        draft.currentUser.expiration = Math.floor(Date.now() / 1000) + refreshTokenAction.payload.expires_in;
        const localStorageUser: IUser = JSON.parse(localStorage.getItem('currentUser') ?? "");
        localStorage.setItem('currentUser',JSON.stringify({
          ...localStorageUser,
          expiration: draft.currentUser.expiration,
          token: draft.currentUser.token,
          refresh_token: draft.currentUser.refresh_token,
        }))
        break;
    }
  });
